// MULTIPLE_STATE_JUSTIFIED: page orchestrator managing dialogs and async operations
import { useState } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig
import { Loader2, Plus, UserPlus, LogOut, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useOrgContext } from '@/hooks/useOrgContext';
import { CreateOrgDialog } from '@/components/organisation/CreateOrgDialog';
import { InviteMemberDialog } from '@/components/organisation/InviteMemberDialog';
import { DeleteOrgDialog } from '@/components/organisation/DeleteOrgDialog';
import { MemberList } from '@/components/organisation/MemberList';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated dataModel lives outside src/; needed for typed Convex IDs
import type { IOrgMember, IOrgInvitationSummary } from '@/@types/organisation';

export function OrganisationSettingsPage() {
  const { currentOrg, isOrgOwner, isInOrg, isLoading } = useOrgContext();
  const currentUser = useQuery(api.users.queries.getCurrentUser);

  // MULTIPLE_STATE_JUSTIFIED: page orchestrator managing dialogs and async operations
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [cancellingInvitationEmail, setCancellingInvitationEmail] = useState<string | null>(null);
  const [cancellingInvitationId, setCancellingInvitationId] = useState<Id<'orgInvitations'> | null>(
    null
  );

  const members = useQuery(
    api.organisations.queries.listMembers,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const pendingInvitations = useQuery(
    api.organisations.invitations.listByOrg,
    currentOrg && isOrgOwner ? { orgId: currentOrg._id } : 'skip'
  );

  const removeMember = useMutation(api.organisations.mutations.removeMember);
  const leaveOrg = useMutation(api.organisations.mutations.leaveOrganisation);
  const verifyAndDeleteOrg = useAction(api.organisations.actions.verifyPasswordAndDeleteOrg);
  const cancelInvitation = useMutation(api.organisations.invitations.cancelInvitation);
  const updateMemberName = useMutation(api.users.mutations.updateMemberName);

  const handleRemoveMember = async (membershipId: Id<'orgMembers'>) => {
    setIsRemoving(true);
    try {
      await removeMember({ membershipId });
    } finally {
      setIsRemoving(false);
    }
  };

  const handleLeaveOrg = async () => {
    if (!currentOrg) return;
    setIsLeaving(true);
    try {
      await leaveOrg({ orgId: currentOrg._id });
    } finally {
      setIsLeaving(false);
    }
  };

  const handleDeleteOrg = async (password: string) => {
    if (!currentOrg) return;
    await verifyAndDeleteOrg({ orgId: currentOrg._id, password });
    setShowDeleteDialog(false);
  };

  const handleCancelInvitation = async () => {
    if (!cancellingInvitationId) return;
    await cancelInvitation({ invitationId: cancellingInvitationId });
    setCancellingInvitationId(null);
    setCancellingInvitationEmail(null);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-3xl py-8">
        <div className="rounded-lg border p-8 text-center">
          <Loader2 aria-hidden="true" className="mx-auto h-8 w-8 animate-spin text-primary" />
          <p className="mt-2 text-muted-foreground">Loading organisation...</p>
        </div>
      </div>
    );
  }

  if (!isInOrg) {
    return (
      <div className="container mx-auto max-w-3xl py-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Organisation</h1>
            <p className="mt-2 text-muted-foreground">
              Create an organisation to collaborate with your team.
            </p>
          </div>

          <div className="rounded-lg border p-8 text-center">
            <p className="text-muted-foreground">You are not part of any organisation yet.</p>
            <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
              <Plus aria-hidden="true" className="mr-2 h-4 w-4" />
              Create Organisation
            </Button>
          </div>
        </div>

        <CreateOrgDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
      </div>
    );
  }

  const orgMembers: IOrgMember[] = members ?? [];
  const allInvitations: IOrgInvitationSummary[] = pendingInvitations ?? [];
  const activePendingInvitations = allInvitations.filter(inv => inv.status === 'pending');

  return (
    <div className="container mx-auto max-w-3xl py-8">
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold">{currentOrg?.name}</h1>
            {currentOrg?.industry && (
              <Badge variant="secondary" className="mt-1">
                {currentOrg.industry}
              </Badge>
            )}
          </div>

          <div className="flex gap-2">
            {isOrgOwner && (
              <Button variant="outline" onClick={() => setShowInviteDialog(true)}>
                <UserPlus aria-hidden="true" className="mr-2 h-4 w-4" />
                Invite Member
              </Button>
            )}

            {!isOrgOwner && (
              <Button
                variant="outline"
                onClick={handleLeaveOrg}
                disabled={isLeaving}
                className="text-destructive hover:text-destructive"
              >
                {isLeaving ? (
                  <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <LogOut aria-hidden="true" className="mr-2 h-4 w-4" />
                )}
                Leave Organisation
              </Button>
            )}

            {isOrgOwner && (
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(true)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 aria-hidden="true" className="mr-2 h-4 w-4" />
                Delete Organisation
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-semibold">Members</h2>
          {members === undefined ? (
            <div className="rounded-lg border p-6 text-center">
              <Loader2 aria-hidden="true" className="mx-auto h-6 w-6 animate-spin text-primary" />
              <span className="sr-only">Loading members...</span>
            </div>
          ) : (
            <MemberList
              members={orgMembers}
              isOwner={isOrgOwner}
              currentUserId={currentUser?._id ?? null}
              onRemoveMember={handleRemoveMember}
              onUpdateName={async (targetUserId, name) => {
                await updateMemberName({ targetUserId, name });
              }}
              isRemoving={isRemoving}
            />
          )}
        </div>

        {isOrgOwner && activePendingInvitations.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">Pending Invitations</h2>
            <div className="divide-y rounded-lg border">
              {activePendingInvitations.map(inv => (
                <div key={inv._id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">{inv.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Invited{' '}
                      {new Date(inv.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setCancellingInvitationId(inv._id);
                      setCancellingInvitationEmail(inv.email);
                    }}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Cancel invitation for ${inv.email}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {currentOrg && (
        <InviteMemberDialog
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
          orgId={currentOrg._id}
        />
      )}

      {currentOrg && (
        <DeleteOrgDialog
          orgName={currentOrg.name}
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDeleteOrg}
        />
      )}

      <AlertDialog
        open={cancellingInvitationId !== null}
        onOpenChange={open => {
          if (!open) {
            setCancellingInvitationId(null);
            setCancellingInvitationEmail(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the invitation for{' '}
              <strong>{cancellingInvitationEmail}</strong>? They will no longer be able to join your
              organisation with this link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Invitation</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelInvitation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
